/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

import { successMessages } from '@/constants/feedbackMessages'
import { Button, Input, ModalBase, NumberInput } from '@components/atoms'
import { yupResolver } from '@hookform/resolvers/yup'
import { editInfochema } from '@schemas/settings/index.schema'
import { dashboardAPI } from '@services/DashboardAPI'
import { modalCreators } from '@store/ducks/modal'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import moment from 'moment'

type Inputs = {
	name: string
	birthdate: string
	whatsapp: string
	phone: string
	email: string
	cpfCnpj: string
}

export const EditInfo = () => {
	const dispatch = useAppDispatch()
	const [editInfo, { isLoading, isSuccess }] =
		dashboardAPI.useEditInfoMutation()
	const {
		modal: { modalName, modalInfo },
	} = useAppSelector((state) => state)

	const {
		register,
		reset,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(editInfochema),
	})

	const onSubmit: SubmitHandler<Inputs> = (data) => {
		editInfo({
			name: data.name,
			birthdate: moment(data?.birthdate).format('YYYY-MM-DD'),
			whatsapp: data.whatsapp || '',
			phone: data.phone || '',
			cep: modalInfo?.cep,
			street: modalInfo?.street,
			number: modalInfo?.number,
			district: modalInfo?.district,
			city: modalInfo?.city,
			uf: modalInfo?.uf,
		})
		dispatch(modalCreators.closeModal())
		reset()
	}

	useEffect(() => {
		if (modalInfo) {
			setValue('name', modalInfo?.name)
			setValue('cpfCnpj', modalInfo?.cpfCnpj || '')
			setValue('email', modalInfo?.email)
			setValue('phone', modalInfo?.phone || '')
			setValue('whatsapp', modalInfo?.whatsapp || '')
			setValue(
				'birthdate',
				moment.utc(modalInfo?.birthdate).format('YYYY-MM-DD')
			)
		}
	}, [modalInfo, setValue])

	useEffect(() => {
		if (isSuccess) {
			dispatch(notificationCreators.show(successMessages.updatedInfo))
		}
	}, [dispatch, isSuccess])

	return (
		<ModalBase
			title='Editar Informações pessoais'
			open={modalName === 'editInfo'}
		>
			<form
				onSubmit={handleSubmit(onSubmit)}
				style={{
					display: 'flex',
					flexDirection: 'column',
					marginTop: 24,
					gap: 24,
					width: '100%',
				}}
			>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label htmlFor='name' id='label-name' style={{ fontSize: '1.2rem' }}>
						Nome completo
					</label>
					<Input
						id='name'
						aria-labelledby='label-name'
						type='text'
						placeholder='Digite seu nome completo'
						error={errors.name?.message}
						{...register('name')}
						css={{ color: '$neutral120' }}
					/>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label
						htmlFor='email'
						id='label-email'
						style={{ fontSize: '1.2rem' }}
					>
						E-mail
					</label>
					<Input
						id='email'
						aria-labelledby='label-email'
						type='email'
						placeholder='Seu e-mail'
						{...register('email')}
						disabled
					/>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label
						htmlFor='birthdate'
						id='label-birthdate'
						style={{ fontSize: '1.2rem' }}
					>
						Data de nascimento
					</label>
					<Input
						id='birthdate'
						aria-labelledby='label-birthdate'
						type='date'
						error={errors.birthdate?.message}
						{...register('birthdate')}
						css={{ color: '$neutral120' }}
						defaultValue={moment.utc(modalInfo?.birthdate).format('YYYY-MM-DD')}
					/>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label
						htmlFor='cpfCnpj'
						id='label-cpfCnpj'
						style={{
							fontSize: '1.2rem',
						}}
					>
						CPF
					</label>
					<NumberInput
						id='cpfCnpj'
						aria-labelledby='label-cpfCnpj'
						placeholder='000.000.000-00'
						type='text'
						{...register('cpfCnpj')}
						onValueChange={(v) => setValue('cpfCnpj', v.value ?? '')}
						defaultValue={modalInfo?.cpfCnpj || ''}
						format='###.###.###-##'
						css={{ pointer: 'none' }}
						disabled
					/>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label
						htmlFor='phone'
						id='label-phone'
						style={{ fontSize: '1.2rem' }}
					>
						Telefone
					</label>
					<NumberInput
						id='phone'
						aria-labelledby='label-phone'
						placeholder='(00) 00000-0000'
						type='tel'
						error={errors.phone?.message}
						{...register('phone')}
						css={{ color: '$neutral120' }}
						onValueChange={(v) => setValue('phone', v.value ?? '')}
						defaultValue={modalInfo?.phone || ''}
						format='(##) #####-####'
					/>
				</div>
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
					<label
						htmlFor='whatsapp'
						id='label-whatsapp'
						style={{ fontSize: '1.2rem' }}
					>
						WhatsApp
					</label>
					<NumberInput
						id='whatsapp'
						type='tel'
						placeholder='(00) 00000-0000'
						error={errors.whatsapp?.message}
						{...register('whatsapp')}
						css={{ color: '$neutral120' }}
						onValueChange={(v) => setValue('whatsapp', v.value)}
						defaultValue={modalInfo?.whatsapp || ''}
						format='(##) #####-####'
					/>
				</div>
				<div style={{ display: 'flex' }}>
					<Button loading={isLoading}>Salvar</Button>
				</div>
			</form>
		</ModalBase>
	)
}
